import { useState } from 'react';
import { CalendarCheck, CalendarX } from 'lucide-react';
import {
  Button,
  DataTable,
  ErrorState,
  FormField,
  MetricCard,
  MetricRow,
  Modal,
  StatusPill,
  TextField,
  Textarea,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatDate } from '../../lib/format/dates';
import { formatCount } from '../../lib/format/money';
import { notify } from '../../services/notify';
import type { FilingRow } from './api';
import { useFilingCalendar, useMarkFiled } from './queries';
import { humanStatus } from './parts';

const WHO: Record<string, string> = { stripe: 'Stripe', kurtz_fargo: 'Kurtz Fargo', internal: 'Internal' };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The return period as people say it: "Sep 2026", "Q3 2026" or "2026". */
export function periodLabel(frequency: string, periodStart: string): string {
  const d = new Date(periodStart);
  const y = d.getUTCFullYear();
  if (frequency === 'annual') return String(y);
  if (frequency === 'quarterly') return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${y}`;
  return `${MONTHS[d.getUTCMonth()]} ${y}`;
}

function columns(lock: string | undefined, onFile: (r: FilingRow) => void): DataTableColumn<FilingRow>[] {
  return [
    {
      id: 'jurisdiction',
      header: 'Jurisdiction',
      width: '1.6fr',
      cell: (r) => (
        <span className="min-w-0">
          <span className="block truncate font-semibold">{r.jurisdictionName || r.jurisdictionRef}</span>
          <span className="block text-xs text-muted">{humanStatus(r.jurisdictionType)}</span>
        </span>
      ),
    },
    { id: 'freq', header: 'Frequency', width: '120px', cell: (r) => humanStatus(r.filingFrequency || '—') },
    { id: 'who', header: 'Who files', width: '130px', cell: (r) => WHO[r.whoFiles] ?? (r.whoFiles || '—') },
    {
      id: 'due',
      header: 'Next due',
      width: '160px',
      cell: (r) =>
        r.nextDueDate ? (
          <span>
            {formatDate(r.nextDueDate, 'UTC')}
            {r.daysUntilDue !== undefined && (
              <span className={`block text-xs ${r.daysUntilDue < 0 ? 'text-danger' : 'text-muted'}`}>
                {r.daysUntilDue < 0 ? `${-r.daysUntilDue} days late` : `in ${r.daysUntilDue} days`}
              </span>
            )}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      id: 'state',
      header: 'Return',
      width: '1.2fr',
      cell: (r) =>
        !r.periodStart ? (
          <span className="text-muted">—</span>
        ) : r.periodFiled ? (
          <span>
            <StatusPill tone="success" dot>
              {periodLabel(r.filingFrequency, r.periodStart)} filed {formatDate(r.filedAt, 'UTC')}
            </StatusPill>
            {r.confirmationNumber && (
              <span className="mt-0.5 block font-mono text-xs text-muted">{r.confirmationNumber}</span>
            )}
          </span>
        ) : r.overdue ? (
          <StatusPill tone="danger" dot>
            {periodLabel(r.filingFrequency, r.periodStart)} overdue
          </StatusPill>
        ) : (
          <StatusPill tone="warning" dot>
            {periodLabel(r.filingFrequency, r.periodStart)} not filed
          </StatusPill>
        ),
    },
    {
      id: 'act',
      header: '',
      width: '120px',
      align: 'right',
      action: true,
      cell: (r) =>
        r.periodStart && !r.periodFiled ? (
          <Button
            variant="ghost-dark"
            size="sm"
            lockedReason={lock}
            onClick={(e) => {
              e.stopPropagation();
              onFile(r);
            }}
          >
            Mark filed
          </Button>
        ) : null,
    },
  ];
}

/**
 * Filing calendar (§7.10): every active registration with its next due date
 * and whether the return due on it is filed. Overdue first.
 */
export function FilingScreen() {
  const { locked } = useSession();
  const q = useFilingCalendar();
  const [filing, setFiling] = useState<FilingRow | null>(null);
  const rows = [...(q.data?.rows ?? [])].sort((a, b) => Number(b.overdue) - Number(a.overdue));
  const t = q.data?.totals;

  return (
    <div className="flex flex-col gap-[18px]">
      <MetricRow>
        <MetricCard
          label="Active registrations"
          value={t ? formatCount(t.activeRegistrations) : q.isError ? '—' : '…'}
          accent="var(--admin-chart-2)"
          icon={<CalendarCheck size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Overdue"
          value={t ? formatCount(t.overdue) : q.isError ? '—' : '…'}
          hint="past due and not filed"
          accent="var(--admin-chart-5)"
          icon={<CalendarX size={15} aria-hidden="true" />}
        />
      </MetricRow>
      <DataTable
        label="Filing calendar"
        minWidth={1000}
        columns={columns(locked('tax.write'), setFiling)}
        rows={rows}
        getRowId={(r) => r.registrationId}
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        emptyLabel="No active registrations."
      />
      {filing && <MarkFiledDialog row={filing} onClose={() => setFiling(null)} />}
    </div>
  );
}

function MarkFiledDialog({ row, onClose }: { row: FilingRow; onClose: () => void }) {
  const create = useMarkFiled();
  const today = new Date().toISOString().slice(0, 10);
  const [filedOn, setFiledOn] = useState(today);
  const [confirmation, setConfirmation] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={`Mark ${row.jurisdictionName || row.jurisdictionRef} ${periodLabel(row.filingFrequency, row.periodStart!)} filed`}
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!filedOn || filedOn > today}
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                {
                  registrationId: row.registrationId,
                  filedOn,
                  confirmationNumber: confirmation.trim(),
                  notes: notes.trim(),
                },
                {
                  onSuccess: () => {
                    notify.success(`${periodLabel(row.filingFrequency, row.periodStart!)} marked filed.`);
                    onClose();
                  },
                },
              )
            }
          >
            Mark filed
          </Button>
        </>
      }
    >
      <p className="mt-0 text-sm text-muted">
        The {humanStatus(row.filingFrequency || 'monthly').toLowerCase()} return due{' '}
        {formatDate(row.nextDueDate, 'UTC')}.
      </p>
      <div className="grid gap-3.5">
        <FormField label="Filed on" htmlFor="filed-on" required>
          <TextField
            id="filed-on"
            type="date"
            surface="white"
            max={today}
            value={filedOn}
            onChange={(e) => setFiledOn(e.target.value)}
          />
        </FormField>
        <FormField label="Confirmation number" htmlFor="filed-conf" optional>
          <TextField
            id="filed-conf"
            surface="white"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </FormField>
        <FormField label="Notes" htmlFor="filed-notes" optional>
          <Textarea
            id="filed-notes"
            surface="white"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormField>
      </div>
      {create.error && <ErrorState error={create.error} compact />}
    </Modal>
  );
}
